// Smart HTML content truncation utility
// This utility provides intelligent truncation that preserves HTML structure and readability

/**
 * Closes any open HTML tags in truncated content
 * @param {string} html - HTML content that might have open tags
 * @returns {string} - HTML with properly closed tags
 */
function closeOpenTags(html) {
  const openTags = [];
  const tagRegex = /<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/g;
  let match = null;

  // Find all tags and track which ones are open
  // eslint-disable-next-line no-cond-assign
  while ((match = tagRegex.exec(html))) {
    const fullTag = match[0];
    const tagName = match[1].toLowerCase();

    // Skip self-closing tags
    if (fullTag.endsWith("/>") || ["img", "br", "hr", "input", "meta", "link"].includes(tagName)) {
      // eslint-disable-next-line no-continue
      continue;
    }

    if (fullTag.startsWith("</")) {
      // Closing tag - remove from open tags
      const index = openTags.lastIndexOf(tagName);
      if (index !== -1) {
        openTags.splice(index, 1);
      }
    } else {
      // Opening tag
      openTags.push(tagName);
    }
  }

  // Close any remaining open tags in reverse order
  let result = html;
  for (let i = openTags.length - 1; i >= 0; i--) {
    result += `</${openTags[i]}>`;
  }

  return result;
}

/**
 * Intelligently truncates HTML content while preserving structure and readability
 * @param {string} htmlContent - The HTML content to truncate
 * @param {number} maxLength - Maximum character length (default: 300)
 * @param {boolean} preserveWords - Whether to preserve whole words (default: true)
 * @param {boolean} preserveSentences - Whether to preserve whole sentences (default: false)
 * @returns {object} - { content, isTruncated, originalLength }
 */
export function smartTruncateHTML(
  htmlContent,
  maxLength = 300,
  preserveWords = true,
  preserveSentences = false
) {
  if (!htmlContent || typeof htmlContent !== "string") {
    return { content: "", isTruncated: false, originalLength: 0 };
  }

  // Strip HTML tags to get plain text for length calculation
  const plainText = htmlContent.replace(/<[^>]*>/g, "");
  const originalLength = plainText.length;

  if (originalLength <= maxLength) {
    return { content: htmlContent, isTruncated: false, originalLength };
  }

  // Find the optimal truncation point
  let truncateAt = maxLength;

  if (preserveSentences) {
    // Find the last complete sentence within the limit
    const sentenceEnders = /[.!?]\s+/g;
    let match = null;
    let lastSentenceEnd = -1;

    // eslint-disable-next-line no-cond-assign
    while ((match = sentenceEnders.exec(plainText))) {
      if (match.index + match[0].length <= maxLength) {
        lastSentenceEnd = match.index + match[0].length;
      } else {
        break;
      }
    }

    if (lastSentenceEnd > 0) {
      truncateAt = lastSentenceEnd;
    }
  } else if (preserveWords) {
    // Find the last complete word within the limit
    const wordBoundary = /\s+/g;
    let match = null;
    let lastWordEnd = -1;

    // eslint-disable-next-line no-cond-assign
    while ((match = wordBoundary.exec(plainText))) {
      if (match.index <= maxLength) {
        lastWordEnd = match.index;
      } else {
        break;
      }
    }

    if (lastWordEnd > 0) {
      truncateAt = lastWordEnd;
    }
  }

  // Now we need to find the corresponding position in the HTML content
  let plainPosition = 0;
  let inTag = false;
  let result = "";

  for (let i = 0; i < htmlContent.length && plainPosition < truncateAt; i++) {
    const char = htmlContent[i];

    if (char === "<") {
      inTag = true;
    } else if (char === ">") {
      inTag = false;
      result += char;
      // eslint-disable-next-line no-continue
      continue;
    }

    result += char;

    if (!inTag) {
      plainPosition++;
    }
  }

  // Ensure we don't break HTML tags - close any open tags
  result = closeOpenTags(result);

  return {
    content: result,
    isTruncated: true,
    originalLength,
    truncatedAt: truncateAt,
  };
}

/**
 * Alternative approach: Truncate by visible text while preserving HTML
 * This is useful when you want to count only visible characters
 * Uses regex-based parsing to avoid DOM dependency
 * @param {string} htmlContent - The HTML content to truncate
 * @param {number} maxVisibleChars - Maximum visible characters
 * @returns {object} - { content, isTruncated, originalVisibleLength }
 */
export function truncateByVisibleText(htmlContent, maxVisibleChars = 300) {
  if (!htmlContent || typeof htmlContent !== "string") {
    return { content: "", isTruncated: false, originalVisibleLength: 0 };
  }

  // Extract visible text by removing HTML tags
  const visibleText = htmlContent.replace(/<[^>]*>/g, "");

  if (visibleText.length <= maxVisibleChars) {
    return {
      content: htmlContent,
      isTruncated: false,
      originalVisibleLength: visibleText.length,
    };
  }

  // Find word boundary near the limit
  let truncateAt = maxVisibleChars;
  const lastSpace = visibleText.lastIndexOf(" ", maxVisibleChars);
  if (lastSpace > maxVisibleChars * 0.8) {
    // Don't go too far back
    truncateAt = lastSpace;
  }

  // Now traverse HTML and build result until we reach truncate point
  let result = "";
  let visibleCount = 0;
  let i = 0;

  while (i < htmlContent.length && visibleCount < truncateAt) {
    const char = htmlContent[i];

    if (char === "<") {
      // We're entering an HTML tag
      const tagEnd = htmlContent.indexOf(">", i);
      if (tagEnd !== -1) {
        // Add the entire tag
        result += htmlContent.substring(i, tagEnd + 1);
        i = tagEnd + 1;
      } else {
        // Malformed HTML, treat as text
        result += char;
        visibleCount++;
        i++;
      }
    } else {
      // Regular character
      result += char;
      visibleCount++;
      i++;
    }
  }

  // Close any open tags
  result = closeOpenTags(result);

  return {
    content: result,
    isTruncated: true,
    originalVisibleLength: visibleText.length,
    truncatedAt: truncateAt,
  };
}

/**
 * Smart email content truncation specifically designed for email content
 * Handles common email patterns and preserves readability
 * @param {string} emailContent - Email HTML content
 * @param {number} maxLength - Maximum character length
 * @returns {object} - { content, isTruncated, originalLength }
 */
export function smartTruncateEmailContent(emailContent, maxLength = 300) {
  if (!emailContent || typeof emailContent !== "string") {
    return { content: "", isTruncated: false, originalLength: 0 };
  }

  // Remove extra whitespace and normalize
  const normalizedContent = emailContent.replace(/\s+/g, " ").replace(/>\s+</g, "><").trim();

  // Get plain text length
  const plainText = normalizedContent.replace(/<[^>]*>/g, "");

  if (plainText.length <= maxLength) {
    return { content: normalizedContent, isTruncated: false, originalLength: plainText.length };
  }

  // Try sentence-based truncation first for better readability
  let result = smartTruncateHTML(normalizedContent, maxLength, true, true);

  // If sentence truncation is too short (less than 70% of desired length),
  // fall back to word truncation
  const resultPlainText = result.content.replace(/<[^>]*>/g, "");
  if (result.isTruncated && resultPlainText.length < maxLength * 0.7) {
    result = smartTruncateHTML(normalizedContent, maxLength, true, false);
  }

  return result;
}

/**
 * Extracts HTML content up to a specific plain text position
 * @param {string} htmlContent - HTML content
 * @param {number} targetPosition - Target position in plain text
 * @returns {string} - Extracted HTML
 */
function extractHtmlToPosition(htmlContent, targetPosition) {
  let result = "";
  let plainPosition = 0;
  let inTag = false;

  for (let i = 0; i < htmlContent.length && plainPosition < targetPosition; i++) {
    const char = htmlContent[i];

    if (char === "<") {
      inTag = true;
    } else if (char === ">") {
      inTag = false;
      result += char;
      // eslint-disable-next-line no-continue
      continue;
    }

    result += char;

    if (!inTag) {
      plainPosition++;
    }
  }

  return result;
}

/**
 * Adjusts break point to avoid breaking inside important HTML elements
 * @param {string} htmlContent - Original HTML content
 * @param {string} plainText - Plain text version
 * @param {number} breakPoint - Current break point
 * @returns {number} - Adjusted break point
 */
function adjustBreakPointForElements(htmlContent, plainText, breakPoint) {
  // Find position in HTML that corresponds to plain text break point
  let htmlPos = 0;
  let plainPos = 0;
  let inTag = false;

  for (let i = 0; i < htmlContent.length && plainPos < breakPoint; i++) {
    const char = htmlContent[i];

    if (char === "<") {
      inTag = true;
    } else if (char === ">") {
      inTag = false;
    } else if (!inTag) {
      plainPos++;
    }

    htmlPos = i;
  }

  // Check if we're inside a link or important element
  const beforeHtml = htmlContent.substring(0, htmlPos);
  const afterHtml = htmlContent.substring(htmlPos);

  // Count unclosed important tags before break point
  const importantTags = ["a", "strong", "b", "em", "i", "span"];
  const openTags = [];

  const tagPattern = /<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/g;
  let match = null;

  // eslint-disable-next-line no-cond-assign
  while ((match = tagPattern.exec(beforeHtml))) {
    const tagName = match[1].toLowerCase();

    if (importantTags.includes(tagName)) {
      if (match[0].startsWith("</")) {
        // Closing tag
        const index = openTags.lastIndexOf(tagName);
        if (index !== -1) {
          openTags.splice(index, 1);
        }
      } else if (!match[0].endsWith("/>")) {
        // Opening tag (not self-closing)
        openTags.push(tagName);
      }
    }
  }

  // If we have unclosed important tags, try to find a better break point
  if (openTags.length > 0) {
    // Look for the next closing tag in the remaining content
    const remainingPattern = new RegExp(`</(${openTags.join("|")})>`, "i");
    const closingMatch = afterHtml.match(remainingPattern);

    if (closingMatch && closingMatch.index < 50) {
      // Don't go too far
      // Adjust break point to after the closing tag
      const adjustedHtmlPos = htmlPos + closingMatch.index + closingMatch[0].length;

      // Convert back to plain text position
      let adjustedPlainPos = 0;
      let inTagAdjusted = false;

      for (let i = 0; i < adjustedHtmlPos && i < htmlContent.length; i++) {
        const char = htmlContent[i];

        if (char === "<") {
          inTagAdjusted = true;
        } else if (char === ">") {
          inTagAdjusted = false;
        } else if (!inTagAdjusted) {
          adjustedPlainPos++;
        }
      }

      return adjustedPlainPos;
    }
  }

  return breakPoint;
}

/**
 * Adds ellipsis to truncated content in a smart way
 * @param {string} htmlContent - HTML content to add ellipsis to
 * @param {string} ellipsis - Ellipsis text to add
 * @returns {string} - Content with ellipsis added
 */
function addEllipsisToContent(htmlContent, ellipsis = "...") {
  if (!htmlContent) return htmlContent;

  // Find the last text node to add ellipsis
  const lastOpenTag = htmlContent.lastIndexOf("<");
  const lastCloseTag = htmlContent.lastIndexOf(">");

  // If content ends with HTML tag, add ellipsis before the closing tags
  if (lastCloseTag > lastOpenTag && lastCloseTag === htmlContent.length - 1) {
    // Find where to insert ellipsis (before closing tags)
    const textContent = htmlContent.replace(/<[^>]*>/g, "");

    // If there's actual text content, find where to add ellipsis
    if (textContent.trim()) {
      // Look for the last bit of text content
      let inTag = false;
      let lastTextPos = -1;

      for (let i = htmlContent.length - 1; i >= 0; i--) {
        const char = htmlContent[i];

        if (char === ">") {
          inTag = true;
        } else if (char === "<") {
          inTag = false;
        } else if (!inTag && char.trim()) {
          lastTextPos = i + 1;
          break;
        }
      }

      if (lastTextPos > 0) {
        return (
          htmlContent.substring(0, lastTextPos) + ellipsis + htmlContent.substring(lastTextPos)
        );
      }
    }
  }

  // Simple case: just append ellipsis
  return htmlContent + ellipsis;
}

/**
 * Advanced HTML-aware truncation with intelligent breaking
 * @param {string} htmlContent - HTML content to truncate
 * @param {number} maxLength - Maximum visible character length
 * @param {object} options - Configuration options
 * @returns {object} - { content, isTruncated }
 */
export function fastHtmlTruncate(htmlContent, maxLength = 300, options = {}) {
  const {
    preferSentences = true,
    preferWords = true,
    minSentenceLength = Math.floor(maxLength * 0.3), // Don't break too early for sentences
    minWordLength = Math.floor(maxLength * 0.7), // Don't break too early for words
    preserveLinks = true,
    preserveFormatting = true,
    addEllipsis = true, // Add ellipsis when content is truncated
    ellipsis = "...", // Customizable ellipsis text
  } = options;

  if (!htmlContent || typeof htmlContent !== "string") {
    return { content: "", isTruncated: false };
  }

  // Clean and normalize content
  const normalizedContent = htmlContent.replace(/\s+/g, " ").replace(/>\s+</g, "><").trim();

  const plainText = normalizedContent.replace(/<[^>]*>/g, "");

  if (plainText.length <= maxLength) {
    return { content: normalizedContent, isTruncated: false };
  }

  // Find the optimal breaking point with multiple strategies
  let breakPoint = maxLength;
  let breakReason = "char-limit";

  if (preferSentences) {
    // Strategy 1: Look for sentence endings
    const sentencePattern = /[.!?]+[\s\n]*(?=[A-Z]|$)/g;
    let match = null;
    let bestSentenceEnd = -1;

    // eslint-disable-next-line no-cond-assign
    while ((match = sentencePattern.exec(plainText))) {
      const endPos = match.index + match[0].length;
      if (endPos <= maxLength && endPos >= minSentenceLength) {
        bestSentenceEnd = endPos;
        breakReason = "sentence-end";
      } else if (endPos > maxLength) {
        break;
      }
    }

    if (bestSentenceEnd > minSentenceLength) {
      breakPoint = bestSentenceEnd;
    }
  }

  if (preferWords && (breakReason === "char-limit" || breakPoint < minWordLength)) {
    // Strategy 2: Look for word boundaries
    const wordPattern = /\s+/g;
    let match = null;
    let bestWordEnd = -1;

    // eslint-disable-next-line no-cond-assign
    while ((match = wordPattern.exec(plainText))) {
      if (match.index <= maxLength && match.index >= minWordLength) {
        bestWordEnd = match.index;
        breakReason = "word-boundary";
      } else if (match.index > maxLength) {
        break;
      }
    }

    if (bestWordEnd > minWordLength) {
      breakPoint = bestWordEnd;
    }
  }

  // Strategy 3: Avoid breaking inside important elements
  if (preserveLinks || preserveFormatting) {
    breakPoint = adjustBreakPointForElements(normalizedContent, plainText, breakPoint);
  }

  // Extract HTML content up to the break point
  let result = extractHtmlToPosition(normalizedContent, breakPoint);

  // Add ellipsis if content is truncated and option is enabled
  if (addEllipsis && result) {
    result = addEllipsisToContent(result, ellipsis);
  }

  return {
    content: closeOpenTags(result),
    isTruncated: true,
    breakReason,
    actualLength: result.replace(/<[^>]*>/g, "").length,
  };
}

/**
 * Ultra-smart truncation that handles complex scenarios
 * @param {string} htmlContent - HTML content to truncate
 * @param {number} maxLength - Maximum character length
 * @returns {object} - { content, isTruncated }
 */
export function ultraSmartTruncate(htmlContent, maxLength = 300, addEllipsis = true) {
  return fastHtmlTruncate(htmlContent, maxLength, {
    preferSentences: true,
    preferWords: true,
    minSentenceLength: Math.floor(maxLength * 0.2),
    minWordLength: Math.floor(maxLength * 0.6),
    preserveLinks: true,
    preserveFormatting: true,
    addEllipsis,
    ellipsis: "...",
  });
}

/**
 * Specialized truncation for addresses and short content
 * Always adds ellipsis and prefers word boundaries
 * @param {string} content - Content to truncate (can be plain text or HTML)
 * @param {number} maxLength - Maximum character length
 * @returns {object} - { content, isTruncated }
 */
export function truncateAddress(content, maxLength = 50) {
  return fastHtmlTruncate(content, maxLength, {
    preferSentences: false, // Addresses don't have sentences
    preferWords: true,
    minWordLength: Math.floor(maxLength * 0.5),
    preserveLinks: false,
    preserveFormatting: false,
    addEllipsis: true,
    ellipsis: "...",
  });
}
